import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, 
  FileText, 
  Image, 
  Trash2, 
  Download, 
  Eye, 
  Plus,
  Camera,
  X,
  CheckCircle,
  AlertCircle,
  Wrench,
  Hammer,
  Shield,
  ClipboardList,
  ScrollText,
  BookOpen,
  Receipt
} from 'lucide-react';
import { vehicleDocumentsAPI } from '../services/api';

const VehicleDocumentsTab = ({ vehicle, onProfilePictureUpdate }) => {
  const [documents, setDocuments] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: 'general',
    file: null
  });
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const documentCategories = [
    { value: 'general', label: 'General Documents' },
    { value: 'maintenance', label: 'Maintenance Records' },
    { value: 'repair', label: 'Repair Records' },
    { value: 'insurance', label: 'Insurance Documents' },
    { value: 'registration', label: 'Registration & Title' },
    { value: 'warranty', label: 'Warranty Information' },
    { value: 'manual', label: 'Owner\'s Manual' },
    { value: 'receipts', label: 'Receipts & Invoices' }
  ];

  // Load documents and profile picture
  useEffect(() => {
    if (vehicle?.id) {
      loadDocuments();
      loadProfilePicture();
    }
  }, [vehicle?.id]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await vehicleDocumentsAPI.getDocuments(vehicle.id);
      // Handle both response.documents and direct array response
      // Ensure documents is always an array
      let documentsData = response.documents || response || [];
      if (!Array.isArray(documentsData)) {
        console.warn('Documents data is not an array:', documentsData);
        documentsData = [];
      }
      setDocuments(documentsData);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
      // Fallback to empty array
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProfilePicture = async () => {
    try {
      const response = await vehicleDocumentsAPI.getProfilePicture(vehicle.id);
      // Handle both response.profile_picture and direct response
      const profilePic = response.profile_picture || response;
      setProfilePicture(profilePic);
    } catch (err) {
      console.error('Error loading profile picture:', err);
      // Profile picture might not exist, which is fine
      setProfilePicture(null);
    }
  };

  const handleFileUpload = async (file, type = 'document') => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    
    if (type === 'document') {
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description);
      formData.append('category', uploadData.category);
    }

    try {
      setUploading(true);
      setError(null); // Clear any previous errors
      
      if (type === 'profile_picture') {
        const response = await vehicleDocumentsAPI.uploadProfilePicture(vehicle.id, formData);
        // Handle both response.profile_picture and direct response
        const profilePic = response.profile_picture || response;
        setProfilePicture(profilePic);
        onProfilePictureUpdate?.(profilePic.url);
      } else {
        const response = await vehicleDocumentsAPI.uploadDocument(vehicle.id, formData);
        // Handle both response.document and direct response
        const newDocument = response.document || response;
        setDocuments(prev => [...prev, newDocument]);
        setShowUploadModal(false);
        setUploadData({ title: '', description: '', category: 'general', file: null });
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await vehicleDocumentsAPI.deleteDocument(vehicle.id, documentId);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      await vehicleDocumentsAPI.deleteProfilePicture(vehicle.id);
      setProfilePicture(null);
      onProfilePictureUpdate?.(null);
    } catch (err) {
      console.error('Error deleting profile picture:', err);
      setError('Failed to delete profile picture');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Lucide, not emoji (DESIGN_SYSTEM.md section 4).
  const getCategoryIcon = (category) => {
    const icons = {
      maintenance: Wrench,
      repair: Hammer,
      insurance: Shield,
      registration: ClipboardList,
      warranty: ScrollText,
      manual: BookOpen,
      receipts: Receipt,
      general: FileText,
    };
    return icons[category] || FileText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        <span className="ml-2 text-ink-muted">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Picture Section */}
      <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
        <h3 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
          <Camera className="w-5 h-5 mr-2" />
          Vehicle Profile Picture
        </h3>
        
        <div className="flex items-center space-x-6">
          {profilePicture ? (
            <div className="relative">
              <img
                src={profilePicture.url || profilePicture}
                alt="Vehicle profile"
                className="w-24 h-24 rounded-lg object-cover border-2 border-border-default"
              />
              <button
                onClick={handleDeleteProfilePicture}
                className="absolute -top-2 -right-2 bg-danger text-white rounded-full p-1 hover:bg-danger/80"
                title="Remove profile picture"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 border-2 border-dashed border-border-default rounded-lg flex items-center justify-center bg-surface-page">
              <Camera className="w-8 h-8 text-ink-muted" />
            </div>
          )}
          
          <div className="flex-1">
            <p className="text-sm text-ink-muted mb-2">
              {profilePicture 
                ? 'Upload a new picture to replace the current one'
                : 'Add a profile picture for your vehicle'
              }
            </p>
            <button
              onClick={() => imageInputRef.current?.click()}
              className="flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
              disabled={uploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Picture'}
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleFileUpload(file, 'profile_picture');
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-ink-soft flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Vehicle Documents
          </h3>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload Document
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-danger-tint border border-danger-border rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="w-4 h-4 text-danger mr-2" />
              <span className="text-danger text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Ensure documents is always an array before rendering */}
        {(() => {
          const safeDocuments = Array.isArray(documents) ? documents : [];
          
          if (safeDocuments.length === 0) {
            return (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-ink-muted mx-auto mb-4" />
                <h4 className="text-lg font-medium text-ink-soft mb-2">No documents uploaded yet</h4>
                <p className="text-ink-muted mb-4">
                  Upload important documents like maintenance records, insurance cards, and receipts to keep them organized.
                </p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center mx-auto px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Your First Document
                </button>
              </div>
            );
          }
          
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {safeDocuments.map((doc) => (
              <div key={doc.id} className="border border-border-default rounded-lg p-4 transition-colors hover:border-border-strong">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    {(() => {
                      const CategoryIcon = getCategoryIcon(doc.category);
                      return <CategoryIcon className="w-5 h-5 mr-2 text-ink-soft flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />;
                    })()}
                    <div>
                      <h4 className="font-medium text-ink-soft text-sm">{doc.title}</h4>
                      <p className="text-small text-ink-muted">
                        {documentCategories.find(cat => cat.value === doc.category)?.label}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="text-ink-muted hover:text-danger p-1"
                    title="Delete document"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                {doc.description && (
                  <p className="text-sm text-ink-muted mb-3 line-clamp-2">{doc.description}</p>
                )}
                
                <div className="flex items-center justify-between text-small text-ink-muted mb-3">
                  <span>{formatFileSize(doc.file_size)}</span>
                  <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={async () => {
                      try {
                        const blob = await vehicleDocumentsAPI.downloadDocument(vehicle.id, doc.id);
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                      } catch (err) {
                        console.error('Error viewing document:', err);
                        setError('Failed to view document');
                      }
                    }}
                    className="flex-1 flex items-center justify-center px-3 py-1.5 text-small bg-surface-muted text-ink-soft rounded hover:bg-surface-muted"
                    title="View document"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const blob = await vehicleDocumentsAPI.downloadDocument(vehicle.id, doc.id);
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = doc.file_name;
                        link.click();
                      } catch (err) {
                        console.error('Error downloading document:', err);
                        setError('Failed to download document');
                      }
                    }}
                    className="flex-1 flex items-center justify-center px-3 py-1.5 text-small bg-accent-tint text-accent rounded hover:bg-accent-tint/80"
                    title="Download document"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </button>
                </div>
              </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-ink-soft">Upload Document</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-ink-muted hover:text-ink-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              if (uploadData.file) {
                handleFileUpload(uploadData.file);
              }
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-1">
                    Document Title *
                  </label>
                  <input
                    type="text"
                    value={uploadData.title}
                    onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-1">
                    Description
                  </label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                    rows="3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-1">
                    Category *
                  </label>
                  <select
                    value={uploadData.category}
                    onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                    required
                  >
                    {documentCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-1">
                    File *
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-border-default rounded-lg p-6 text-center cursor-pointer hover:border-border-default transition-colors"
                  >
                    {uploadData.file ? (
                      <div>
                        <CheckCircle className="w-8 h-8 text-success mx-auto mb-2" />
                        <p className="text-sm font-medium text-ink-soft">{uploadData.file.name}</p>
                        <p className="text-small text-ink-muted">{formatFileSize(uploadData.file.size)}</p>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-ink-muted mx-auto mb-2" />
                        <p className="text-sm text-ink-muted">Click to select a file</p>
                        <p className="text-small text-ink-muted">PDF, JPG, PNG up to 10MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file && file.size <= 10 * 1024 * 1024) { // 10MB limit
                        setUploadData(prev => ({ ...prev, file }));
                      } else if (file) {
                        alert('File size must be less than 10MB');
                      }
                    }}
                    required
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 border border-border-default text-ink-soft rounded-lg hover:bg-surface-page"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadData.file || !uploadData.title}
                  className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDocumentsTab;
